import {Component, ElementRef, EventEmitter, Input, OnInit, ViewChild} from '@angular/core';
import {ActivityLogService} from '../../services/activity-log.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {CustomersService} from '../../../customers/services/customers.service';
import {CustomerConvertActivity} from '../../data/customerConvertActivity';
import {CustomerConversionType} from '../../data/customerConversionType';
import {CrudActivity} from '../../data/crudActivity';
import {ActivityDTO} from '../../data/activityDTO';
import {EntityType} from '../../../app-common/data/EntityType';
import {OperationTypeEnum} from '../../data/operationTypeEnum';
import {CommunicationService} from '../../../communication/services/communication.service';
import {CategoryService} from '../../../category/services/category/category.service';
import {JobService} from '../../../job/services/job/job.service';
import {SnippetService} from '../../../snippet/services/snippet/snippet.service';
import {FlagActivity} from '../../data/flagActivity';
import {FlagColor} from '../../../flag/data/flagColor';
import {FlagService} from '../../../flag/services/flag.service';
import {SignalRService} from '../../../app-common/services/signalr/signal-r.service';
import {UserService} from '../../../user/services/user/user.service';
import {ElectronService} from 'ngx-electron';
import {LoggerService} from '../../../logging/services/logger.service';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {PostService} from '../../../docs/services/post/post.service';

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.scss']
})
export class ActivityLogComponent implements OnInit {

  @Input() expandEvent: EventEmitter<void>;

  public logEntries: SafeHtml[] = [];
  @ViewChild('log') logElement: ElementRef;
  @ViewChild('sound') soundElement: ElementRef;
  closed: boolean = true;

  constructor(public activityLogService: ActivityLogService,
              public customerService: CustomersService,
              public communicationService: CommunicationService,
              public categoryService: CategoryService,
              public jobService: JobService,
              public snippetService: SnippetService,
              public postService: PostService,
              public flagService: FlagService,
              public signalRService: SignalRService,
              public userService: UserService,
              private electronService: ElectronService,
              public sanitizer: DomSanitizer,
              private loggerService: LoggerService) {
    this.activityLogService = activityLogService;
    this.customerService = customerService;
    this.sanitizer = sanitizer;
  }

  get isElectron() {
    return this.electronService.isElectronApp;
  }

  async fetchData() {
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.ActivityLogComponent, 'Fetching data');
    const entries = await this.activityLogService.getActivityLogsAfter(new Date((new Date()).getTime() - 24 * 60 * 60 * 1000));
    for (const entry of entries) {
      await this.processActivityDTO(entry, false);
    }
    this.scrollToBottomOfLog();
  }

  async ngOnInit() {
    this.activityLogService.firstOpenEvent.subscribe(async () => {
      await this.fetchData();
    });
    this.signalRService.crudHandler.onReceive.subscribe(async (data: ActivityDTO) => {
      await this.processActivityDTO(data);
      this.scrollToBottomOfLog();
    });
    // this.signalRService.flagHandler.onReceive.subscribe(async (data: ActivityDTO) => {
    //   await this.processActivityDTO(data);
    //   this.scrollToBottomOfLog();
    // });
    this.signalRService.customerConvertHandler.onReceive.subscribe(async (data: ActivityDTO) => {
      await this.processActivityDTO(data);
      this.scrollToBottomOfLog();
    });

    this.expandEvent.subscribe(async () => {
      this.closed = false;
    });
  }

  scrollToBottomOfLog() {
    setTimeout(() => {
      this.logElement.nativeElement.scrollTop = this.logElement.nativeElement.scrollHeight;
    }, 100);
  }

  private async processCustomerConvertActivity(entry: ActivityDTO, resultHTML: string) {
    const decodedEntry = entry.activityData as CustomerConvertActivity;
    const customer = await this.customerService.getCustomer(decodedEntry.customerId);
    const customerName = `${customer.customer.contactName} - ${customer.customer.companyName}`;
    resultHTML += ` has converted the ${decodedEntry.customerConversionType === CustomerConversionType.ToLead ? 'contact' : 'lead'}
          ${this.createEntityHTML(customerName, EntityType.Customer, entry.url)} to a ${decodedEntry.customerConversionType === CustomerConversionType.ToLead ? 'lead' : 'contact'}`;
    return resultHTML;
  }

  private async processFlagActivity(entry: ActivityDTO, resultHTML: string) {
    const decodedEntry = entry.activityData as FlagActivity;
    const entityType = this.convertEntityTypeEnumToString(decodedEntry.entityType);
    const entityName = await this.getEntityName(decodedEntry.entityType, decodedEntry.entityId);
    resultHTML += ` has changed the flag status on the ${entityType.toLowerCase()} ${this.createEntityHTML(entityName, decodedEntry.entityType, entry.url)}
    from ${this.createFlagHTML(decodedEntry.oldFlagValue, decodedEntry.oldFlagColor)} to ${this.createFlagHTML(decodedEntry.newFlagValue, decodedEntry.newFlagColor)}`;
    return resultHTML;
  }

  private async processCrudActivity(entry: ActivityDTO, resultHTML: string) {
    const decodedEntry = entry.activityData as CrudActivity;
    const entityType = this.convertEntityTypeEnumToString(decodedEntry.entityType);
    const operationType = this.convertOperationTypeEnumToString(decodedEntry.operation);
    const entityName = await this.getEntityName(decodedEntry.entityType, decodedEntry.entityId);
    resultHTML += ` has ${operationType.toLowerCase()} the ${entityType.toLowerCase()} ${this.createEntityHTML(entityName, decodedEntry.entityType, entry.url)}.`;
    return resultHTML;
  }

  private convertEntityTypeEnumToString(entityType: EntityType) {
    switch (entityType) {
      case EntityType.Attachment:
        return 'Attachment';
      case EntityType.Communication:
        return 'Communication';
      case EntityType.Category:
        return 'Category';
      case EntityType.Customer:
        return 'Customer';
      case EntityType.Job:
        return 'Job';
      case EntityType.PaymentLinkTemplate:
        return 'Payment Link Template';
      case EntityType.Snippet:
        return 'Snippet';
      case EntityType.Appointment:
        return 'Appointment';
      case EntityType.Document:
        return 'Document';
    }
  }

  private convertEntityTypeEnumToIcon(entityType: EntityType) {
    switch (entityType) {
      case EntityType.Attachment:
        return '<i class="fas fa-paperclip"></i>';
      case EntityType.Communication:
        return '<i class="fas fa-phone"></i>';
      case EntityType.Category:
        return '<i class="fas fa-filter"></i>';
      case EntityType.Customer:
        return '<i class="fas fa-user-tie"></i>';
      case EntityType.Job:
        return '<i class="fas fa-briefcase"></i>';
      case EntityType.PaymentLinkTemplate:
        return '<i class="fas fa-document"></i>';
      case EntityType.Snippet:
        return '<i class="fas fa-scissors"></i>';
      case EntityType.Appointment:
        return '<i class="fas fa-calendar-check"></i>';
      case EntityType.Document:
        return '<i class="fas fa-info"></i>';
    }
  }

  private convertOperationTypeEnumToString(operationType: OperationTypeEnum) {
    switch (operationType) {
      case OperationTypeEnum.Mention:
        return 'Mentioned';
      case OperationTypeEnum.Comment:
        return 'Commented';
      case OperationTypeEnum.InitialAssigned:
        return 'Assigned';
      case OperationTypeEnum.AssignedUpdate:
        return 'Updated';
      case OperationTypeEnum.Add:
        return 'Added';
      case OperationTypeEnum.Edit:
        return 'Edited';
      case OperationTypeEnum.Delete:
        return 'Deleted';
      case OperationTypeEnum.Reparent:
        return 'Reparented';

    }
  }

  createMentionHTML(username: string) {
    return `<span class="mention" data-mention="@${username}">@${username}</span>`;
  }

  createEntityHTML(entityName: string, entityType: EntityType, entityUrl: string) {
    return `<span class="entity-mention">${this.convertEntityTypeEnumToIcon(entityType)} ${entityName}</span>`;
  }

  createDateHTML(date: Date) {
    const dateObj = new Date(date);
    const dateString = `${dateObj.getMonth() + 1}-${dateObj.getDate()}-${dateObj.getFullYear()} ${dateObj.getHours()}:${dateObj.getMinutes()}:${dateObj.getSeconds()} ${dateObj.toLocaleString('en-US', { hour: 'numeric', hour12: true })}`;
    return `<span>${dateString}</span>`;
  }

  private async getEntityName(entityType: EntityType, entityId: number) {
    switch (entityType) {
      case EntityType.Communication:
        const comm = await this.communicationService.getCommunication(entityId, true);
        const commCustomer = comm.customerDto;
        return `${commCustomer.customer.contactName} - ${commCustomer.customer.companyName} | ${comm.communicationDto.communication.personName} | ${comm.communicationDto.communication.title}`;
      case EntityType.Category:
        const category = await this.categoryService.getCategory(entityId, true);
        return category.category.name;
      case EntityType.Customer:
        const customer = await this.customerService.getCustomer(entityId, true);
        return `${customer.customer.contactName} - ${customer.customer.companyName}`;
      case EntityType.Job:
        const dto = await this.jobService.getJob(entityId, true);
        const jobCustomer = await this.customerService.getCustomer(dto.job.customerId);
        return `${jobCustomer.customer.contactName} - ${jobCustomer.customer.companyName} | ${dto.job.title}`;
      case EntityType.PaymentLinkTemplate:
        break;
      case EntityType.Snippet:
        const snippetDto = await this.snippetService.getSnippet(entityId, true);
        return snippetDto.snippet.name;
      case EntityType.Document:
        const docDto = await this.postService.getPostById(entityId, true);
        return docDto.post.title;

    }
    return '';
  }

  private createFlagHTML(flagValue: boolean, flagColor: FlagColor) {
    if (!flagValue) {
      return `<b><span style="color: var(--ui-color-content)"><i class="far fa-flag"></i> none</span></b>`;
    } else {
      return `<span style="color: ${this.flagService.getColor(flagColor)}"><i class="fas fa-flag"></i> <b>${this.flagService.flagColorKeys[flagColor]}</b></span>`;
    }
  }

  private async processActivityDTO(entry: ActivityDTO, playSound = true) {
    let resultHTML: string;
    resultHTML = `${this.createDateHTML(entry.activityData.timestamp)} | ${this.createMentionHTML(entry.authorName)} `;

    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.ActivityLogComponent, `Processing ${entry.activityType} activity entry`);
    switch (entry.activityType) {
      case 'CustomerConvertActivity':
        resultHTML = await this.processCustomerConvertActivity(entry, resultHTML);
        break;
      case 'CrudActivity':
        resultHTML = await this.processCrudActivity(entry, resultHTML);
        break;
      case 'FlagActivity':
        resultHTML = await this.processFlagActivity(entry, resultHTML);
        break;
      default:
        break;
    }

    this.logEntries.push(this.sanitizer.bypassSecurityTrustHtml(resultHTML));

    if (!playSound) {
      return;
    }
    const audioSettings = localStorage.getItem('log-audio') === 'true';
    if (!audioSettings) {
      return;
    }

    if (entry.activityData.authorId !== this.userService.currentUser.accountId) {
      this.soundElement.nativeElement.play();
    }
    this.loggerService.logMessage(LogLevel.INFO, LogSourceType.COMPONENT, LogSource.ActivityLogComponent, `Processed ${entry.activityType} activity entry`);
  }

  close() {
   this.closed = true;
  }
}
